﻿import { OutputFormatType } from "diff2html/lib/types";
import { ObjectStateEnum } from "../../../Shared/Scripts/Enums/object-state-enum";
import { IModelBaseWithId } from "../../../Shared/Scripts/Models/model-base-with-id";
import { SoftoolsValidationResult } from "../../../Shared/Scripts/Results/softools-validation-result";

export interface PublishIndexModel extends IModelBaseWithId {
    identifier: string;
    siteIdentifier: string;
    checklist: ChecklistResultModel;
    publishedAppSnapshots: PublishedAppSnapshotModel[];
    previousPublishedApps: PreviousPublishedAppModel[];
    publishedAppSelectOptions: PublishedAppSelectOption[];
}

export interface PublishedAppSnapshotModel {
    publishedAppVersionNumber: number;
    usernameOrEmail: string;
    snapshotDateFormatted: string;
    notes: string;
    state: ObjectStateEnum;
}

export interface PreviousPublishedAppModel {
    appVersionNumber: number;
    usernameOrEmail: string;
    publishedDateFormatted: string;
    notes: string;
    status: PublishedAppStatusEnum;
    statusDescription: string;
    rolledBackAppVersionNumber?: number;
}

export interface PublishedAppSelectOption {
    appVersionNumber: number;
    publishedDateFormatted: string;
    notes: string;
    userId: string;
    usernameOrEmail: string;
    publishedNumberOfDaysAgo: number;
}

export type SnapshotFilterOptions = 90 | 60 | 30 | 7;

export interface PublishedAppSnapshotModalData {
    targetPublishedAppVersionNumber?: number;
    snapshotFilterNumberOfDaysAgo: SnapshotFilterOptions;
    inProgress: boolean;
}

export interface RollbackModalData {
    targetPublishedAppSnapshotVerionNumber?: number;
}

export interface AppDiffModalData {
    oldDataFormatted: string;
    newDataFormatted: string;
    
    jsonDiffPatchDiffLineCount: number;
    diffMaxChanges: number;

    // Config options for; https://www.npmjs.com/package/diff2html
    codeDiffOutputFormat: OutputFormatType;
    isSideBySideOutputFormat: boolean;
}

export interface AppDiffInfoResult extends SoftoolsValidationResult {
    oldData: string;
    newData: string;

    /**
     * Diff2Html npm package has `diffMaxChanges` property we can set for the client diff.
     * However, with large diff dataset the UI gets into an unresponsive state - until the diff parse/ html/ render process is completed 
     * - to mitigate this - first we should check diff line count on the server using nuget JsonDiffPatchDiff.
     */
    jsonDiffPatchDiffLineCount: number;

    /**
     * Max number of lines to allow in the diff.
     * Diff2Html npm package has `diffMaxChanges` property we can set,
     * to limit the client side parsed diff model - it will not show when over this limit.
     */
    diffMaxChanges: number;
}

export interface PublishWithNoteModalData {
    notes: string;
    takeASnapshot: boolean;
}

export interface ChecklistResultModel {
    isAppIconSet: boolean;
    isDescriptionSet: boolean;
    isTitleFieldSet: boolean;
    isReportSet: boolean;
    isFormSet: boolean;
    hasExpressionErrors: boolean;
    isMaximumSortableFieldsSet: boolean;
    isReportFiltersConfigValid: boolean;

    /**
     * Whether the publish checklist is in a valid state to allow user to Publish a new version of the App.
     */
    isValidState: boolean;
}

export interface PublishVersionCommandModel {
    targetAppVersion: number;
    notes?: string;
    takeASnapshot: boolean;
}

export interface PublishVersionCommandResult extends SoftoolsValidationResult {
    publishedAppSnapshot: PublishedAppSnapshotModel;
}

export interface SnapshotCommandModel {
    targetPublishedAppVersionNumber: number;
}

export interface DeleteSnapshotCommandModel {
    targetPublishedAppSnapshotVersionNumber: number;
}

export interface RollbackCommandModel {
    targetPublishedAppSnapshotVerionNumber: number;
}

export interface DownloadAppQueryResult extends SoftoolsValidationResult {
    blobSharedDownloadUrl?: string;
}

export interface ExportAppJsonResult {
    fileName: string;
    json: string;
}

export interface AppPublishStatusInfo {
    /**
     * AppPublishStatus: Ready or InProgress.
     */
    hasUnpublishedChanges?: boolean;

    /**
     * HasUnpublishedChanges, determined by whether the current App's PublishedAppVersionNumber is less than the current App's AppVersionNumber.
     */
    latestAvailableVersion?: number;

    /**
     * Latest Version Number of the App.
     * Incremented on every POST/ PATCH/ DELETE request when the App changes.
     */
    appPublishStatus?: AppPublishStatusEnum;

    /**
     * Will contain the most recent published version, which may have 1..n supported languages.
     * Optional: PublishedTranslatedApps information is only relevant within the Publish folder context
     * where we will present the most recent publish information to the user.
     */
    publishedTranslatedApps?: PublishedTranslatedAppStatusInfo[];

    /**
     * Disable 'TakeASnapshot' at the point of publish, when maximum Snapshots count has been met.
     */
    maximumSnapshotsCountExceeded?: boolean;

    /**
     * Previous Published Version Number to be used by App Diff Info when comparing the past two Published Versions.
     */
    previousPublishedVersionNumber?: number;

    /**
     * Whether or not the App has any Expression Errors present.
     */
    appHasExpressionErrors?: boolean;
}

export interface PublishedTranslatedAppStatusInfo {
    appVersionNumber: number;
    culture: string;
    usernameOrEmail: string;
    status: PublishedAppStatusEnum;
    statusDescription: string;
    errorMessage: string;
    elapsedMilliseconds: number;
    notes: string;
    publishedDate: string;
    rolledBackAppVersionNumber?: number;
}

export interface PublishStatusOverview {
    publishedAppVersionNumber: number;
    publishedByUsernameOrEmail: string;
    publishedDate: string;
}

export enum AppPublishStatusEnum {
    Ready = 0,
    InProgress = 1
}

export enum PublishedAppStatusEnum {
    Starting = 0,
    InProgress = 1,
    Success = 2,
    Error = 3,
    Cancelled = 4
}
